import React from 'react';
import PropTypes from 'prop-types';
import { fleetPanelData, hasInventory } from '../inventory/fleetPanelData';
import {
  REVIEW_SEGMENTS,
  STATE_TONE,
  fleetSize,
  formatDateTime,
  initialsFor,
  ownerLabel,
  reviewCounts,
  reviewedCount,
  versionTotal
} from './projectListModel';

const TONES = ['info', 'ok', 'warn', 'bad', 'prevent', 'plan', 'predict'];

function toneFor(id) {
  let hash = 0;
  String(id || '')
    .split('')
    .forEach(ch => {
      hash = (hash * 31 + ch.charCodeAt(0)) % 9973;
    });
  return TONES[hash % TONES.length];
}

function monogram(label) {
  const words = String(label || '?')
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length > 1) return (words[0][0] + words[1][0]).toUpperCase();
  return (words[0] || '?').slice(0, 2).toUpperCase();
}

export const VendorMark = ({ vendorId, label = '', size = 32 }) => (
  <span
    className={`pl-vmark pl-tone-${toneFor(vendorId)}`}
    style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }}
    title={label || vendorId}
    aria-hidden="true"
  >
    {monogram(label || vendorId)}
  </span>
);
VendorMark.propTypes = { vendorId: PropTypes.string.isRequired, label: PropTypes.string, size: PropTypes.number };

export const Owner = ({ userId, currentUserEmail, avatarOnly = false }) => (
  <span className="pl-owner" title={userId || ''}>
    <span className="pl-avatar" aria-hidden="true">
      {initialsFor(userId)}
    </span>
    {!avatarOnly && <span className="bz-truncate">{ownerLabel(userId, currentUserEmail)}</span>}
  </span>
);
Owner.propTypes = { userId: PropTypes.string, currentUserEmail: PropTypes.string, avatarOnly: PropTypes.bool };

export const StatePill = ({ state }) => {
  const tone = STATE_TONE[state] || 'neutral';
  return <span className={`bz-pill ${tone === 'neutral' ? 'pl-pill--neutral' : `bz-pill--${tone}`}`}>{state}</span>;
};
StatePill.propTypes = { state: PropTypes.string.isRequired };

// Review progress out of the project's Available Versions (`available`, from
// useAvailableVersions): undefined while it loads, null if the lookup failed
// (then the versions opened so far stand in).
export const ReviewBar = ({ project, available, compact = false }) => {
  const counts = reviewCounts(project);
  const reviewed = reviewedCount(project);
  const total = versionTotal(project, available);
  // Until the total lands, show a dedicated loading state instead of numbers
  // that would change shape a moment later.
  if (available === undefined) {
    return (
      <div className="pl-rb" aria-busy="true" aria-label="Counting versions">
        <div className="pl-rb__track pl-skel" />
        <div className="pl-rb__meta">
          <span className={`pl-skel pl-skel--text${compact ? '' : ' pl-skel--wide'}`} />
        </div>
      </div>
    );
  }
  if (total === 0) {
    return (
      <div className="pl-rb">
        <div className="pl-rb__track" />
        <div className="pl-rb__meta">
          <span>No newer versions</span>
        </div>
      </div>
    );
  }
  const notReviewed = total - reviewed;
  const parts = REVIEW_SEGMENTS.map(s => ({ ...s, n: s.key === 'notReviewed' ? notReviewed : counts[s.key] })).filter(
    s => s.n > 0
  );
  return (
    <div className="pl-rb" title={parts.map(s => `${s.n} ${s.label.toLowerCase()}`).join(' · ')}>
      <div className="pl-rb__track">
        {parts.map(s => (
          <span key={s.key} className={`pl-rb__seg pl-rb__seg--${s.key}`} style={{ flexGrow: s.n }} />
        ))}
      </div>
      <div className="pl-rb__meta">
        {compact ? (
          <span>
            <b>{reviewed}</b>/{total} reviewed
          </span>
        ) : (
          <span>
            <b>{reviewed}</b> of {total} version{total === 1 ? '' : 's'} reviewed
          </span>
        )}
        {!compact && notReviewed > 0 && <span className="pl-rb__todo">{notReviewed} to review</span>}
      </div>
    </div>
  );
};
ReviewBar.propTypes = { project: PropTypes.object.isRequired, available: PropTypes.number, compact: PropTypes.bool };

export const SourceBadge = ({ project }) => {
  const drift = fleetPanelData(project);
  return (
    <span className="pl-source">
      {hasInventory(project) ? (
        <span
          className="bz-pill bz-pill--plan"
          title={`From inventory · snapshot ${formatDateTime(project.inventorySnapshot.fetchedAt)}`}
        >
          {fleetSize(project)} CIs
        </span>
      ) : (
        <span className="bz-pill pl-pill--neutral">Manual</span>
      )}
      {drift && drift.hasDrift && (
        <span className="bz-pill bz-pill--warn" title={`Inventory majority is now ${drift.majorityVersion}`}>
          <span className="bz-dot" />
          Drift
        </span>
      )}
    </span>
  );
};
SourceBadge.propTypes = { project: PropTypes.object.isRequired };

export const VersionPath = ({ project, versionLabel, compact = false }) => (
  <span className={`pl-vers${compact ? ' pl-vers--compact' : ''}`}>
    <span className="pl-ver" title={`Baseline version ${versionLabel(project, project.versionId)}`}>
      {versionLabel(project, project.versionId)}
    </span>
    <span className="pl-vers__arrow">→</span>
    {project.acceptedVersionId ? (
      <span
        className="pl-ver pl-ver--target"
        title={`Accepted upgrade target ${versionLabel(project, project.acceptedVersionId)}`}
      >
        {versionLabel(project, project.acceptedVersionId)}
      </span>
    ) : (
      <span className="pl-vers__none">no target yet</span>
    )}
  </span>
);
VersionPath.propTypes = {
  project: PropTypes.object.isRequired,
  versionLabel: PropTypes.func.isRequired,
  compact: PropTypes.bool
};

// The project name opens the project; modified clicks keep the browser's
// new-tab behaviour via the real href.
export const NameLink = ({ project, onOpen }) => (
  <a
    href={`/plan/${project.id}`}
    className="pl-namelink"
    onClick={e => {
      e.stopPropagation();
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      onOpen(project);
    }}
    onDoubleClick={e => e.stopPropagation()}
  >
    {project.name}
  </a>
);
NameLink.propTypes = { project: PropTypes.object.isRequired, onOpen: PropTypes.func.isRequired };
